import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  output,
  signal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { EllipsisDirective } from '../../../directives/ellipsis/ellipsis';
import { isLegacyCfi } from '../../../helpers/cfi';
import { findComponentPosition } from '../../../helpers/locator';
import { questionIconFor, quizModeFor } from '../../../helpers/quiz';
import { quizLocator } from '../../../helpers/quiz-locator';
import { legacyCfiProgression } from '../../../helpers/readium';
import { isHighlight } from '../../../helpers/selections';
import type { QuizMaterialResponse } from '../../../models/quiz';
import { MetadataService } from '../../../services/metadata/metadata';
import { QuizService } from '../../../services/quiz/quiz';
import { ChapterService } from '../../../services/readium/chapter/chapter';
import { ReaderNavigatorService } from '../../../services/readium/navigator/reader-navigator';
import { SelectionsService } from '../../../services/selections/selections';

export interface QuestionsListItem {
  quiz: QuizMaterialResponse;
  icon: string;
  chapterTitle: string;
  progress: number;
}

@Component({
  selector: 'nr-questions-list',
  imports: [TranslatePipe, EllipsisDirective, DecimalPipe],
  templateUrl: './questions-list.html',
  styleUrl: './questions-list.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class QuestionsListComponent {
  private static readonly INSTANCE_ID_LENGTH = 8;

  private readonly quizService = inject(QuizService);
  private readonly metadataService = inject(MetadataService);
  private readonly selectionsService = inject(SelectionsService);
  private readonly readerNavigatorService = inject(ReaderNavigatorService);
  private readonly chapterService = inject(ChapterService);
  // nr-navigation-tabs is mounted twice (header and footer): ids must be unique per instance
  // or both menus bind to the same trigger and open together.
  private readonly instanceId = crypto
    .randomUUID()
    .slice(0, QuestionsListComponent.INSTANCE_ID_LENGTH);
  private opening = false;

  readonly nrNavigated = output();
  readonly pendingDelete = signal<QuizMaterialResponse | undefined>(undefined);

  readonly items = computed<QuestionsListItem[]>(() =>
    this.quizService
      .quizzes()
      .map(({ quiz }) => ({
        quiz,
        icon: questionIconFor(quiz.materialKind),
        chapterTitle: this.chapterTitleFor(quiz),
        progress: quiz.selection.ebookReadingPercentage,
      }))
      .sort((a, b) => a.progress - b.progress),
  );

  readonly deleteTitleKey = computed(() => `QUESTIONS.DELETE_TITLE_${this.pendingDeleteSuffix()}`);
  readonly deleteBodyKey = computed(() => `QUESTIONS.DELETE_BODY_${this.pendingDeleteSuffix()}`);

  triggerId(quizId: string): string {
    return `question-menu-${this.instanceId}-${quizId}`;
  }

  async openQuestion(quiz: QuizMaterialResponse): Promise<void> {
    if (this.opening) return;
    this.opening = true;
    try {
      const highlights = this.selectionsService.selections().filter(isHighlight);
      const locator = quizLocator(quiz, highlights, {
        positions: this.metadataService.positions(),
        componentNames: this.metadataService.componentNames(),
      });
      if (locator && !(await this.readerNavigatorService.goToLocator(locator))) return;
      if (locator) await this.goToLegacyCfiPage(quiz.selection.percent ?? '');
      this.quizService.startEditQuiz(quiz);
      this.nrNavigated.emit();
    } finally {
      this.opening = false;
    }
  }

  // A locator cannot express a legacy CFI, so quizLocator opens its chapter at the start; once
  // Readium has shown the chapter (goToLocator resolves after that), move to the page where the
  // placement shows the question.
  private async goToLegacyCfiPage(percent: string): Promise<void> {
    if (!isLegacyCfi(percent)) return;
    const frames = this.readerNavigatorService.getCurrentFrameElements();
    const progression = legacyCfiProgression(frames, percent);
    if (progression !== undefined) await this.readerNavigatorService.goToProgression(progression);
  }

  requestDelete(quiz: QuizMaterialResponse): void {
    this.pendingDelete.set(quiz);
  }

  cancelDelete(): void {
    this.pendingDelete.set(undefined);
  }

  async confirmDelete(): Promise<void> {
    const quiz = this.pendingDelete();
    this.pendingDelete.set(undefined);
    if (!quiz) return;
    const relId = this.metadataService.metadata()?.userConfig?.bookId ?? '';
    await this.quizService.deleteQuiz(quiz.id, relId);
  }

  private pendingDeleteSuffix(): string {
    return quizModeFor(this.pendingDelete()?.materialKind).toUpperCase();
  }

  private chapterTitleFor(quiz: QuizMaterialResponse): string {
    const position = findComponentPosition(
      quiz.selection.componentId,
      this.metadataService.positions(),
      this.metadataService.componentNames(),
    );
    return this.chapterService.getTitleFromHref(position?.href);
  }
}
